import { useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { ROUTES } from '@/lib/constants/routes';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { OTP_UI } from '@/lib/constants/otp';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { Button } from '@/components/ui/Button';
import { OtpInput } from '@/components/ui/OtpInput';
import { useCountdown } from '@/hooks/useCountdown';
import { getApiErrorCode, mapApiError } from '@/lib/utils/error.utils';
import { useSendSharedProfileOtp } from '@/features/shared-profile/hooks/useSendSharedProfileOtp';
import { useVerifySharedProfileOtp } from '@/features/shared-profile/hooks/useVerifySharedProfileOtp';
import { SharedProfileIdentityHeader } from '@/features/shared-profile/components/SharedProfileIdentityHeader';

interface SharedProfileVerifyLocationState {
  email?: string;
  companyName?: string;
}

const VERIFY_ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.OTP_INVALID]: UI_STRINGS.SHARED_PROFILE.ERROR_INVALID,
  [API_ERROR_CODES.OTP_NOT_FOUND]: UI_STRINGS.SHARED_PROFILE.ERROR_INVALID,
  [API_ERROR_CODES.OTP_EXPIRED]: UI_STRINGS.SHARED_PROFILE.ERROR_EXPIRED,
  [API_ERROR_CODES.OTP_LOCKED]: UI_STRINGS.SHARED_PROFILE.ERROR_LOCKED,
};

/** Codes after which waiting out the cooldown makes no sense - resend now. */
const RESEND_UNLOCK_CODES = new Set<string>([API_ERROR_CODES.OTP_EXPIRED, API_ERROR_CODES.OTP_LOCKED]);

/**
 * "Enter Your Verification Code" - Shared Startup Profile OTP-verify screen
 * (MYN-58 screen 3). Reuses `OtpInput`/`useCountdown` and the resend/cooldown
 * mechanics from `InviteVerifyOtpPage`, in the simpler single-card layout the
 * design shows (no AuthHero/SectionCard treatment - that's specific to the
 * signup-flow screens).
 */
export function SharedProfileVerifyOtpPage() {
  const { token = '' } = useParams<{ token: string }>();
  const location = useLocation();
  const state = location.state as SharedProfileVerifyLocationState | null;
  const gatePath = ROUTES.SHARED_PROFILE.replace(':token', token);

  const verify = useVerifySharedProfileOtp(token);
  const resend = useSendSharedProfileOtp(token);
  const S = UI_STRINGS.SHARED_PROFILE;

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string>();
  const cooldown = useCountdown(OTP_UI.RESEND_COOLDOWN_SECONDS);
  const verifyErrorCode = getApiErrorCode(verify.error);
  const resendReady =
    cooldown.expired || (verifyErrorCode !== undefined && RESEND_UNLOCK_CODES.has(verifyErrorCode));

  // Arriving without an email means the gate step was skipped (direct nav or
  // a refresh dropped the router state) - send the user back to the gate.
  if (!state?.email) {
    return <Navigate to={gatePath} replace />;
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!new RegExp(`^\\d{${OTP_UI.CODE_LENGTH}}$`).test(code)) {
      setCodeError(`Enter the ${OTP_UI.CODE_LENGTH}-digit code`);
      return;
    }
    setCodeError(undefined);
    verify.mutate(code);
  };

  const handleResend = () => {
    resend.mutate(undefined, { onSuccess: () => cooldown.reset() });
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4 py-16">
      <div className="flex w-full max-w-130 flex-col items-center gap-8 rounded-card bg-white p-12 shadow-card">
        <SharedProfileIdentityHeader companyName={state.companyName ?? ''} />

        <div className="h-px w-full bg-input-border" />

        <form onSubmit={handleSubmit} noValidate className="flex w-full flex-col items-center gap-4 text-center">
          <h2 className="font-sans text-2xl font-bold text-navy">{S.OTP_HEADING}</h2>
          <p className="text-sm text-subtext">
            {S.OTP_SUBTITLE_PREFIX}
            <span className="font-semibold text-navy">{state.email}</span>
            {S.OTP_SUBTITLE_SUFFIX}
          </p>

          {verify.isError && (
            <p role="alert" className="text-sm text-danger">
              {mapApiError(verify.error, VERIFY_ERROR_BY_CODE, S.ERROR)}
            </p>
          )}
          {resend.isSuccess && (
            <p role="status" className="text-sm text-dkgreen">
              {S.RESENT}
            </p>
          )}

          <OtpInput
            label={S.OTP_HEADING}
            value={code}
            onChange={setCode}
            length={OTP_UI.CODE_LENGTH}
            groupSize={OTP_UI.CODE_LENGTH}
            error={codeError}
          />

          <Button
            type="submit"
            variant="green"
            disabled={verify.isPending}
            className="w-full py-3.5 text-base font-semibold"
          >
            {verify.isPending ? S.VERIFYING_CODE : S.VERIFY_CODE}
          </Button>

          <p className="text-sm text-slate">
            {S.NO_CODE}{' '}
            {resendReady ? (
              <Button
                type="button"
                variant="link"
                disabled={resend.isPending}
                onClick={handleResend}
              >
                {resend.isPending ? S.RESENDING : S.RESEND}
              </Button>
            ) : (
              <span className="font-semibold text-navy">
                {S.RESEND_IN_PREFIX}
                {Math.floor(cooldown.remaining / 60)}:{String(cooldown.remaining % 60).padStart(2, '0')}
              </span>
            )}
          </p>
        </form>
      </div>
    </div>
  );
}
